export const RESTAURANT_SETUP_TOUR_STORAGE_KEY = "soup_restaurant_setup_tour";

const pendingKey = restaurantId => `soup_restaurant_setup_tour_pending_${restaurantId}`;

const safeGet = key => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    console.warn("Could not read restaurant setup tour storage:", error);
    return null;
  }
};

const safeSet = (key, value) => {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.warn("Could not save restaurant setup tour storage:", error);
    return false;
  }
};

const safeRemove = key => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.warn("Could not clear restaurant setup tour storage:", error);
  }
};

const notifyChange = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("restaurant-setup-tour-change"));
};

export const createDefaultRestaurantSetupDraft = () => ({
  roomCount: 1,
  rooms: [
    {
      name: "",
      defaultSeats: 2,
      tables: [{ seats: 2 }]
    }
  ],
  schedules: [],
  scheduleDraft: {
    days: [],
    startTime: "12:00",
    endTime: "15:00"
  },
  slotInterval: "00:15",
  slotCapacity: 20,
  averageStayRules: [],
  averageStayDraft: {
    people: 1,
    minutes: 90
  },
  definitions: {
    limitPeopleReservation: 8,
    autoAcceptLimit: 4,
    autoAcceptMinHours: 2,
    autoAcceptFutureDays: 30,
    babyChairs: 0,
    babyChairsPerReservation: 0,
    timezone: ""
  },
  phoneNotifications: {
    enabled: false,
    countryCode: "351",
    number: ""
  },
  emailNotifications: {
    enabled: false,
    email: ""
  }
});

export const markRestaurantSetupTourPending = restaurantId => {
  if (!restaurantId) return;
  safeSet(pendingKey(restaurantId), "1");
};

export const isRestaurantSetupTourPending = restaurantId => {
  if (!restaurantId) return false;
  return safeGet(pendingKey(restaurantId)) === "1";
};

export const consumeRestaurantSetupTourPending = restaurantId => {
  if (!isRestaurantSetupTourPending(restaurantId)) return false;
  safeRemove(pendingKey(restaurantId));
  return true;
};

export const startRestaurantSetupTour = restaurantId => {
  if (!restaurantId) return;
  safeSet(
    RESTAURANT_SETUP_TOUR_STORAGE_KEY,
    JSON.stringify({
      active: true,
      restaurantId: String(restaurantId),
      phase: "setup",
      pageKey: "roomCount",
      reservationStep: 0,
      draft: createDefaultRestaurantSetupDraft()
    })
  );
  notifyChange();
};

export const readRestaurantSetupTour = () => {
  try {
    const raw = safeGet(RESTAURANT_SETUP_TOUR_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.active || !parsed?.restaurantId) return null;

    // Migrate the earlier spotlight-only tutorial state without crashing users
    // who happened to refresh while that older version was active.
    const phase = parsed.phase === "reservation" ? "reservation" : "setup";

    return {
      active: true,
      restaurantId: String(parsed.restaurantId),
      phase,
      pageKey: parsed.pageKey || "roomCount",
      reservationStep: Number.isFinite(Number(parsed.reservationStep))
        ? Number(parsed.reservationStep)
        : 0,
      draft: parsed.draft || createDefaultRestaurantSetupDraft()
    };
  } catch (error) {
    console.warn("Could not read restaurant setup tour state:", error);
    return null;
  }
};

const writeTour = (tour, shouldNotify = true) => {
  if (!tour?.restaurantId) return;
  safeSet(
    RESTAURANT_SETUP_TOUR_STORAGE_KEY,
    JSON.stringify({
      ...tour,
      active: true,
      restaurantId: String(tour.restaurantId)
    })
  );
  if (shouldNotify) notifyChange();
};

export const updateRestaurantSetupTourPage = (restaurantId, pageKey) => {
  const current = readRestaurantSetupTour();
  if (!current || String(current.restaurantId) !== String(restaurantId)) return;
  writeTour({ ...current, phase: "setup", pageKey });
};

export const saveRestaurantSetupTourDraft = (restaurantId, draft) => {
  const current = readRestaurantSetupTour();
  if (!current || String(current.restaurantId) !== String(restaurantId)) return;
  // Draft changes happen often while typing. Avoid broadcasting a global event
  // for every keystroke; the mounted wizard already owns the live draft state.
  writeTour({ ...current, draft }, false);
};

export const startRestaurantReservationTour = restaurantId => {
  const current = readRestaurantSetupTour();
  if (!current || String(current.restaurantId) !== String(restaurantId)) return;
  writeTour({
    ...current,
    phase: "reservation",
    reservationStep: 0
  });
};

export const updateRestaurantReservationTourStep = (restaurantId, step) => {
  const current = readRestaurantSetupTour();
  if (!current || String(current.restaurantId) !== String(restaurantId)) return;
  writeTour({
    ...current,
    phase: "reservation",
    reservationStep: Number(step) || 0
  });
};

// Backwards-compatible alias used by older code/builds.
export const updateRestaurantSetupTourStep = updateRestaurantReservationTourStep;

export const finishRestaurantSetupTour = () => {
  safeRemove(RESTAURANT_SETUP_TOUR_STORAGE_KEY);
  notifyChange();
};
